import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';

export default function ChatBox({ socket, className, initialMessages = [] }) {
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([
    'Welcome to simple bomberman',
    ...initialMessages
  ]);
  const [messageInput, setMessageInput] = useState('');
  
  // Ensure messages is always an array
  const safeMessages = useMemo(() => Array.isArray(messages) ? messages : [], [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Handle sending messages
  const handleSendMessage = useCallback((e) => {
    e.preventDefault();
    if (messageInput.trim() && socket) {
      socket.emit('message', messageInput);
      setMessageInput('');
    }
  }, [messageInput, socket]);

  // Handle socket events for chat
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (msg) => {
      setMessages((prev) => [...prev, msg]);
    };

    socket.on('message', handleMessage);

    return () => {
      socket.off('message', handleMessage);
    };
  }, [socket]);

  useEffect(() => {
    scrollToBottom();
  }, [safeMessages]);

  return (
    <div className={`bg-gray-900/80 backdrop-blur-lg rounded-2xl border border-gray-700/50 shadow-2xl h-full min-h-[600px] flex flex-col w-full ${className}`}>
      {/* Chat Header */}
      <div className="border-b border-gray-700/50 p-4">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <i className="fas fa-comments text-cyan-400"></i>
          Game Chat
        </h3>
        <p className="text-gray-400 text-sm">Talk with other players</p>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {safeMessages?.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <i className="fas fa-comment-slash text-3xl mb-3"></i>
            <p>No messages yet</p>
            <p className="text-sm">Start a conversation!</p>
          </div>
        ) : (
          safeMessages?.map((msg, index) => (
            <div 
              key={index} 
              className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/30 hover:bg-gray-800/70 transition-colors duration-200"
            >
              <div className="text-gray-300 text-sm leading-relaxed">
                {msg}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t border-gray-700/50 py-4 px-2 overflow-hidden">
        <form onSubmit={handleSendMessage} className="flex gap-3">
          <input 
            id="message-field"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            className="flex-1 bg-gray-800/50 border border-gray-600/50 rounded-lg p-2 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200"
            autoComplete="off" 
            placeholder="Type a message..."
            maxLength={200}
          />
          <button 
            type="submit" 
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-medium px-6 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
            disabled={!messageInput?.trim()}
          >
            <i className="fas fa-paper-plane"></i>
            {/* <span className="hidden sm:inline">Send</span> */}
          </button>
        </form>
        <div className="text-xs text-gray-500 mt-2">
          {messageInput?.length || 0}/200 characters
        </div>
      </div>
    </div>
  );
}
